import { useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, TextareaField } from "@fawkes/ui";

import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";

/**
 * Het onderdeel Allergieën: waar dit dier niet tegen kan.
 *
 * <p>Dit bestond tot nu toe nergens. Allergieën belandden in de vrije kenmerken of in een logboekregel,
 * en daar zijn ze niet te onderscheiden van "houdt niet van kip". Voor een pension is dat verschil niet
 * cosmetisch: wie een hond met een kippenallergie kippenbrok voert heeft een probleem dat niemand had
 * hoeven hebben. De api kreeg er een eigen tabel voor ({@code dier_allergie}).
 *
 * <p><b>Stof en reactie staan los.</b> "Kip" zegt wat je niet moet voeren; "zwelling om de ogen binnen
 * een uur" zegt waar je op moet letten als het tóch gebeurt. Op de tegel staan de stoffen, want dat is
 * de vraag bij het voeren; de reactie staat in het venster, waar je hem leest als er iets aan de hand is.
 *
 * <p>Het onderdeel toont altijd een status: rood zodra er een allergie bekend is. Dat is geen alarm maar
 * een markering -- een dier met een allergie vraagt bij elke maaltijd aandacht, en dat is precies het
 * soort ding dat een ring of een statuslijst moet kunnen laten opvallen.
 */

interface Allergie {
  id: number;
  stof: string;
  reactie: string | null;
  ernst: string | null;
  vastgesteldOp: string | null;
  aangemaaktDoor: string | null;
  gewijzigdDoor: string | null;
}

export function AllergieenOnderdeel({ dierId }: { dierId: number }) {
  const { data: rechten } = usePermissions<string[]>();
  const def = COMPONENT_PER_SLEUTEL.allergieen;
  const magWijzigen = !def.recht || (rechten ?? []).includes(def.recht);

  const { data, refetch } = useCustom<Allergie[]>({ url: `animals/${dierId}/allergieen`, method: "get" });
  const lijst = Array.isArray(data?.data) ? data.data : [];
  const [open, setOpen] = useState(false);

  return (
    <>
      <DierOnderdeel
        def={def}
        waarde={lijst.length > 0 ? lijst.map((a) => a.stof).join(", ") : null}
        herkomst={
          lijst.length > 0
            ? lijst.find((a) => a.reactie)?.reactie ?? undefined
            : undefined
        }
        // Geen groen vinkje bij een lege lijst: "geen allergieen bekend" is iets anders dan "gecontroleerd
        // en er zijn er geen", en een vinkje leest als het tweede.
        status={lijst.length > 0 ? "verlopen" : undefined}
        magSchrijven={magWijzigen}
        onActie={() => setOpen(true)}
      />
      {open ? (
        <AllergieVenster
          dierId={dierId}
          lijst={lijst}
          onSluit={() => setOpen(false)}
          onGewijzigd={() => void refetch?.()}
        />
      ) : null}
    </>
  );
}

/**
 * De allergieën beheren: toevoegen, wijzigen en weghalen.
 *
 * <p>Het venster blijft open na elke wijziging, want wie allergieën invoert heeft er meestal meer dan
 * één en zou anders per stuk opnieuw moeten beginnen. De lijst ververst na elke bewerking, zodat wat er
 * staat altijd is wat de database heeft.
 *
 * <p>Weghalen vraagt een bevestiging in de knop zelf: het is onomkeerbaar, en een allergie die per
 * ongeluk verdwijnt merkt niemand tot het misgaat.
 */
function AllergieVenster({ dierId, lijst, onSluit, onGewijzigd }: {
  dierId: number;
  lijst: Allergie[];
  onSluit: () => void;
  onGewijzigd: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [bewerkt, setBewerkt] = useState<Allergie | "nieuw" | null>(null);
  const [wegHalen, setWegHalen] = useState<number | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const verwijder = (id: number) => {
    setFout(null);
    mutate(
      {
        url: `animals/${dierId}/allergieen/${id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { setWegHalen(null); onGewijzigd(); },
        onError: () => setFout(t("dierTegel.allergie.fout")),
      },
    );
  };

  return (
    <Modal open title={t("dierTegel.allergie.titel")} onClose={onSluit}
      footer={<Button variant="secondary" onClick={onSluit}>{t("common.close")}</Button>}
    >
      <div className="do-invoer">
        {lijst.length === 0 ? (
          <p className="do-invoer__hint">{t("dierTegel.allergie.nogGeen")}</p>
        ) : (
          <ul className="do-allergielijst">
            {lijst.map((a) => (
              <li key={a.id} data-testid={`allergie-${a.id}`}>
                <span className="do-allergielijst__tekst">
                  <strong>{a.stof}</strong>
                  {a.ernst && <span className="do-allergielijst__ernst">{a.ernst}</span>}
                  {a.reactie && <span>{a.reactie}</span>}
                </span>
                <button type="button" className="do-allergielijst__knop" onClick={() => setBewerkt(a)}>
                  {t("common.edit")}
                </button>
                {wegHalen === a.id ? (
                  <button
                    type="button"
                    className="do-allergielijst__knop do-allergielijst__knop--rood"
                    disabled={bezig}
                    onClick={() => verwijder(a.id)}
                  >
                    {t("dierTegel.allergie.zekerWeten")}
                  </button>
                ) : (
                  <button type="button" className="do-allergielijst__knop" onClick={() => setWegHalen(a.id)}>
                    {t("common.delete")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
        {bewerkt ? (
          <AllergieFormulier
            dierId={dierId}
            allergie={bewerkt === "nieuw" ? null : bewerkt}
            onKlaar={() => { setBewerkt(null); onGewijzigd(); }}
            onAnnuleer={() => setBewerkt(null)}
          />
        ) : (
          <Button variant="secondary" onClick={() => setBewerkt("nieuw")}>
            {t("dierTegel.allergie.toevoegen")}
          </Button>
        )}
      </div>
    </Modal>
  );
}

/** Eén allergie invullen. Alleen de stof is verplicht; de api weigert een lege. */
function AllergieFormulier({ dierId, allergie, onKlaar, onAnnuleer }: {
  dierId: number;
  allergie: Allergie | null;
  onKlaar: () => void;
  onAnnuleer: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [stof, setStof] = useState(allergie?.stof ?? "");
  const [reactie, setReactie] = useState(allergie?.reactie ?? "");
  const [ernst, setErnst] = useState(allergie?.ernst ?? "");
  const [vastgesteldOp, setVastgesteldOp] = useState(allergie?.vastgesteldOp ?? "");
  const [fout, setFout] = useState<string | null>(null);

  const leeg = (v: string) => (v.trim() === "" ? null : v.trim());

  const bewaar = () => {
    if (stof.trim() === "") {
      setFout(t("dierTegel.allergie.stofVerplicht"));
      return;
    }
    setFout(null);
    mutate(
      {
        url: allergie ? `animals/${dierId}/allergieen/${allergie.id}` : `animals/${dierId}/allergieen`,
        method: allergie ? "put" : "post",
        values: {
          stof: stof.trim(),
          reactie: leeg(reactie),
          ernst: leeg(ernst),
          vastgesteldOp: leeg(vastgesteldOp),
        },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: onKlaar, onError: () => setFout(t("dierTegel.allergie.fout")) },
    );
  };

  return (
    <div className="do-allergieform">
      <Field label={t("dierTegel.allergie.stof")} value={stof} maxLength={150}
        data-testid="allergie-stof" onChange={(e) => setStof(e.target.value)} />
      <TextareaField label={t("dierTegel.allergie.reactie")} value={reactie} rows={2} maxLength={500}
        onChange={(e) => setReactie(e.target.value)} />
      <Field label={t("dierTegel.allergie.ernst")} value={ernst} maxLength={20}
        onChange={(e) => setErnst(e.target.value)} />
      <Field label={t("dierTegel.allergie.vastgesteldOp")} type="date" value={vastgesteldOp}
        onChange={(e) => setVastgesteldOp(e.target.value)} />
      <p className="do-invoer__hint">{t("dierTegel.allergie.hint")}</p>
      {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
      <div className="do-allergieform__knoppen">
        <Button variant="secondary" onClick={onAnnuleer}>{t("common.cancel")}</Button>
        <Button onClick={bewaar} disabled={bezig}>{t("common.save")}</Button>
      </div>
    </div>
  );
}
